/**
 * Admin → Sites.
 *
 * - admin do tenant: vê e gerencia sites do próprio tenant (sem sidebar).
 * - super_admin: vê todos + sidebar de tenants pra filtrar; pode escolher
 *   tenant ao criar.
 *
 * Modo de visualização (persistido em localStorage):
 *   - 'tree'  (default): split panel com sidebar de tenants + cards de sites
 *   - 'table' (fallback): tabela clássica com todas as colunas
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, LayoutGrid, Pencil, Plus, Rows3, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { sitesApi } from "@/api/sites";
import { apiErrorMessage } from "@/api/client";
import { useDevices, useEquipments, useSites, useTenants } from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { Site, SiteCreate, Tenant } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { Card, CardBody } from "@/components/Card";
import { DataTable, type Column } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { Field, Input, Select } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SiteAdminCard } from "@/components/SiteAdminCard";
import { TenantsSidebar, type SelectedTenantNode } from "@/components/TenantsSidebar";
import { formatDate, shortId } from "@/lib/format";

type ViewMode = "tree" | "table";
const VIEW_MODE_KEY = "envpulse:sites-view-mode";

function loadViewMode(): ViewMode {
  try {
    const raw = localStorage.getItem(VIEW_MODE_KEY);
    return raw === "table" ? "table" : "tree";
  } catch {
    return "tree";
  }
}

export function AdminSitesPage() {
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);

  // super_admin escolhe um tenant pra filtrar / contextualizar criação.
  // Filtra sites, equipments (contagem) e devices (contagem) por tenant.
  const [pickedTenantId, setPickedTenantId] = useState<string>("");
  const tenantFilter = auth.isSuperAdmin ? pickedTenantId || undefined : undefined;

  const sites = useSites(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  // Contagens agregadas nos cards
  const equipments = useEquipments(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  const devices = useDevices(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  const qc = useQueryClient();

  const [editTarget, setEditTarget] = useState<Site | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Site | null>(null);
  const [createPrefill, setCreatePrefill] = useState<{ tenant_id?: string } | null>(null);

  // Modo de visualização
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch {}
  }, [viewMode]);

  const [selectedNode, setSelectedNode] = useState<SelectedTenantNode>({ kind: "all" });

  const createMut = useMutation({
    mutationFn: (p: SiteCreate) => sitesApi.create(p),
    onSuccess: () => {
      toast.success("Site criado");
      void qc.invalidateQueries({ queryKey: ["sites"] });
      setIsCreating(false);
      setCreatePrefill(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: SiteCreate }) =>
      sitesApi.update(id, p),
    onSuccess: () => {
      toast.success("Site atualizado");
      void qc.invalidateQueries({ queryKey: ["sites"] });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => sitesApi.remove(id),
    onSuccess: () => {
      toast.success("Site deletado");
      void qc.invalidateQueries({ queryKey: ["sites"] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const tenantNameById = useMemo(
    () => new Map((tenants.data ?? []).map((t) => [t.id, t.name])),
    [tenants.data],
  );

  // Contagens por site
  const equipmentsCountBySite = useMemo(() => {
    const m = new Map<string, number>();
    for (const eq of equipments.data ?? []) {
      m.set(eq.site_id, (m.get(eq.site_id) ?? 0) + 1);
    }
    return m;
  }, [equipments.data]);

  const devicesCountBySite = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of devices.data ?? []) {
      m.set(d.site_id, (m.get(d.site_id) ?? 0) + 1);
    }
    return m;
  }, [devices.data]);

  // Filtragem
  const filtered = useMemo(() => {
    let list = sites.data ?? [];
    if (viewMode === "tree" && selectedNode.kind === "tenant") {
      list = list.filter((s) => s.tenant_id === selectedNode.id);
    }
    return list;
  }, [sites.data, viewMode, selectedNode]);

  // ---------- Colunas do modo tabela ----------
  const columns: Column<Site>[] = [
    { key: "name", header: "Nome", cell: (r) => <span className="font-medium text-slate-900 dark:text-slate-100">{r.name}</span> },
    { key: "slug", header: "Slug", cell: (r) => <code className="text-xs text-slate-600 dark:text-slate-400">{r.slug}</code> },
    { key: "address", header: "Endereço", cell: (r) => <span className="text-slate-600 dark:text-slate-400">{r.address ?? "—"}</span>, hideOnMobile: true },
  ];
  if (auth.isSuperAdmin) {
    columns.push({
      key: "tenant",
      header: "Tenant",
      cell: (r) => <span className="text-xs text-slate-600 dark:text-slate-400">{tenantNameById.get(r.tenant_id) ?? shortId(r.tenant_id)}</span>,
      hideOnMobile: true,
    });
  }
  columns.push(
    { key: "created_at", header: "Criado em", cell: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(r.created_at)}</span>, hideOnMobile: true },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
        </div>
      ),
    },
  );

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Sites"
        description="Locais físicos onde ficam os ativos (lojas, depósitos, hospitais...)."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={clsx(
                  "flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-xs font-medium",
                  viewMode === "tree"
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 dark:text-slate-300 hover:bg-surface-100 dark:hover:bg-slate-800",
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Árvore
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={clsx(
                  "flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-xs font-medium",
                  viewMode === "table"
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 dark:text-slate-300 hover:bg-surface-100 dark:hover:bg-slate-800",
                )}
              >
                <Rows3 className="h-3.5 w-3.5" />
                Tabela
              </button>
            </div>
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => {
              setCreatePrefill(null);
              setIsCreating(true);
            }}>
              Novo site
            </Button>
          </div>
        }
      />

      {/* Seletor de tenant (só super_admin) — filtra sites, equipments e devices */}
      {auth.isSuperAdmin && (
        <Card className="mb-4">
          <CardBody>
            <Field
              label={
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand-600" />
                  Tenant
                </span>
              }
            >
              <Select
                value={pickedTenantId}
                onChange={(e) => {
                  setPickedTenantId(e.target.value);
                  // Ao trocar tenant, volta seleção pra 'Todos' — o nó anterior
                  // pode não existir mais no novo tenant.
                  setSelectedNode({ kind: "all" });
                }}
                options={[
                  { value: "", label: "Todos os tenants" },
                  ...(tenants.data ?? []).map((t) => ({
                    value: t.id,
                    label: `${t.name} (${t.slug})`,
                  })),
                ]}
              />
            </Field>
          </CardBody>
        </Card>
      )}

      {viewMode === "table" ? (
        <DataTable
          data={filtered}
          columns={columns}
          loading={sites.isLoading}
          rowKey={(r) => r.id}
          emptyTitle="Nenhum site cadastrado"
        />
      ) : (
        <SitesTreeSplit
          isSuperAdmin={auth.isSuperAdmin}
          tenants={tenants.data ?? []}
          allSites={sites.data ?? []}
          filteredSites={filtered}
          tenantNameById={tenantNameById}
          equipmentsCountBySite={equipmentsCountBySite}
          devicesCountBySite={devicesCountBySite}
          selected={selectedNode}
          onSelect={setSelectedNode}
          onEdit={(s) => setEditTarget(s)}
          onDelete={(s) => setDeleteTarget(s)}
          onCreate={(prefill) => {
            setCreatePrefill(prefill);
            setIsCreating(true);
          }}
        />
      )}

      <SiteFormModal
        key={
          isCreating
            ? `create-${createPrefill?.tenant_id ?? ""}`
            : `edit-${editTarget?.id ?? "none"}`
        }
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
          setCreatePrefill(null);
        }}
        title={isCreating ? "Novo site" : "Editar site"}
        initial={editTarget}
        prefill={createPrefill}
        showTenantSelect={auth.isSuperAdmin && isCreating}
        tenantOptions={(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(payload) => {
          if (editTarget) {
            updateMut.mutate({ id: editTarget.id, p: payload });
          } else {
            createMut.mutate(payload);
          }
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar site?"
        message={
          deleteTarget
            ? `Vai apagar o site '${deleteTarget.name}', ativos e devices vinculados.`
            : ""
        }
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- Tree split panel ------------------------------------------------

interface SitesTreeSplitProps {
  isSuperAdmin: boolean;
  tenants: Tenant[];
  allSites: Site[];
  filteredSites: Site[];
  tenantNameById: Map<string, string>;
  equipmentsCountBySite: Map<string, number>;
  devicesCountBySite: Map<string, number>;
  selected: SelectedTenantNode;
  onSelect: (node: SelectedTenantNode) => void;
  onEdit: (site: Site) => void;
  onDelete: (site: Site) => void;
  onCreate: (prefill: { tenant_id?: string } | null) => void;
}

function SitesTreeSplit({
  isSuperAdmin, tenants, allSites, filteredSites, tenantNameById,
  equipmentsCountBySite, devicesCountBySite,
  selected, onSelect, onEdit, onDelete, onCreate,
}: SitesTreeSplitProps) {
  const rightTitle = (() => {
    if (selected.kind === "all") return "Todos os sites";
    return tenantNameById.get(selected.id) ?? "Tenant";
  })();

  const canCreateContextual = isSuperAdmin && selected.kind === "tenant";
  const createLabel =
    selected.kind === "tenant" ? `Novo site em ${rightTitle}` : "Novo site";

  // Pra tenant comum a sidebar de tenants é inútil (só tem 1) — mostra grid full.
  if (!isSuperAdmin) {
    return (
      <SitesGrid
        sites={filteredSites}
        tenantNameById={tenantNameById}
        equipmentsCountBySite={equipmentsCountBySite}
        devicesCountBySite={devicesCountBySite}
        showTenantChip={false}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <Card>
        <CardBody className="p-3">
          <TenantsSidebar
            tenants={tenants}
            sites={allSites}
            selected={selected}
            onSelect={onSelect}
          />
        </CardBody>
      </Card>

      <div className="min-w-0">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {rightTitle}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {filteredSites.length} site(s)
            </p>
          </div>
          {canCreateContextual && (
            <Button
              size="sm"
              icon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => onCreate({ tenant_id: selected.kind === "tenant" ? selected.id : undefined })}
            >
              {createLabel}
            </Button>
          )}
        </div>

        <SitesGrid
          sites={filteredSites}
          tenantNameById={tenantNameById}
          equipmentsCountBySite={equipmentsCountBySite}
          devicesCountBySite={devicesCountBySite}
          showTenantChip={selected.kind === "all"}  // só mostra chip quando ver tudo
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

function SitesGrid({
  sites, tenantNameById, equipmentsCountBySite, devicesCountBySite,
  showTenantChip, onEdit, onDelete,
}: {
  sites: Site[];
  tenantNameById: Map<string, string>;
  equipmentsCountBySite: Map<string, number>;
  devicesCountBySite: Map<string, number>;
  showTenantChip: boolean;
  onEdit: (site: Site) => void;
  onDelete: (site: Site) => void;
}) {
  if (sites.length === 0) {
    return (
      <Card>
        <CardBody className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
          Nenhum site cadastrado.
        </CardBody>
      </Card>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {sites.map((s) => (
        <SiteAdminCard
          key={s.id}
          site={s}
          tenantName={showTenantChip ? (tenantNameById.get(s.tenant_id) ?? null) : null}
          equipmentsCount={equipmentsCountBySite.get(s.id) ?? 0}
          devicesCount={devicesCountBySite.get(s.id) ?? 0}
          onEdit={() => onEdit(s)}
          onDelete={() => onDelete(s)}
        />
      ))}
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

function SiteFormModal({
  open,
  onClose,
  title,
  initial,
  prefill,
  showTenantSelect,
  tenantOptions,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: Site | null;
  /** Prefill contextual pro create (ex.: 'novo site em Tenant X') */
  prefill?: { tenant_id?: string } | null;
  showTenantSelect?: boolean;
  tenantOptions: { value: string; label: string }[];
  submitting?: boolean;
  onSubmit: (p: SiteCreate) => void;
}) {
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [tenantId, setTenantId] = useState<string>(
    initial?.tenant_id ?? prefill?.tenant_id ?? tenantOptions[0]?.value ?? "",
  );

  useEffect(() => {
    setSlug(initial?.slug ?? "");
    setName(initial?.name ?? "");
    setAddress(initial?.address ?? "");
    setTenantId(initial?.tenant_id ?? prefill?.tenant_id ?? tenantOptions[0]?.value ?? "");
  }, [initial, prefill, tenantOptions]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: SiteCreate = {
      slug,
      name,
      address: address || null,
    };
    if (showTenantSelect) payload.tenant_id = tenantId;
    onSubmit(payload);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="site-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="site-form" onSubmit={handleSubmit} className="space-y-4">
        {showTenantSelect && (
          <Field label="Tenant" required>
            <Select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              required
              options={tenantOptions}
              placeholder="Selecione o tenant"
            />
          </Field>
        )}
        <Field label="Nome" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Loja Centro" />
        </Field>
        <Field
          label="Slug"
          required
        >
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} required pattern="[a-zA-Z0-9_-]+" placeholder="loja_centro" />
        </Field>
        <Field label="Endereço">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua Principal, 123" />
        </Field>
      </form>
    </Modal>
  );
}
